import type { A } from "ts-toolbelt"
import type {
  PaginationQuery,
  HasNextPaginatedResponse,
} from "@/utils/utilityTypes"
import { api } from "@/api"
import type { Ref } from "vue"
import { useQuery } from "@tanstack/vue-query"
import type {
  TransactionMethod,
  TransactionStatus,
  TransactionType,
} from "../sharedTypes/transactions"

export type ListTransactionsResponseItem = {
  id: string
  invoiceId: string
  type: TransactionType
  createdAt: Date
  status: TransactionStatus
  amountTransactedGross: number
  amountTransactedGratuity: number
  amountTransactedTotal: number
  transactionMethod: TransactionMethod | null
  cardScheme: string | null
  cardNumberLast4: string
  orderId: string | null
  donationId: string | null
  currency: string
  isDcc: boolean
  isDccFieldsPopulated: boolean
  dccCardCurrencyAmount: number
  dccCardholderCurrency: string
}

export type TransactionQuery = A.Compute<
  PaginationQuery &
    Partial<{
      start?: Date | string
      end?: Date | string
    }>
>

export const listTransactions = async (
  query: TransactionQuery
) => {
  const { body } = await api.get<
    HasNextPaginatedResponse<
      RecursivelyConvertDatesToStrings<ListTransactionsResponseItem>
    >
  >("/transactions/", {
    query,
  })

  return {
    ...body,
    items: body.items.map((transaction) => ({
      ...transaction,
      createdAt: DateFns.parseISO(transaction.createdAt),
    })),
  }
}

export const useTransactions = (
  query: TransactionQuery | Ref<TransactionQuery>
) =>
  useQuery({
    queryKey: [BASE_TRANSACTIONS_QUERY_KEY, query] as const,
    queryFn: ({ queryKey: [_, query_] }) =>
      listTransactions(query_),
    refetchInterval: 60 * 1000,
    staleTime: 0,
  })
